import { AgingReport, Month } from "common";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAtom, useSetAtom } from "jotai";

import { useUser } from "./user";
import { reportsPerFacilityPayerState, ReportsState } from "./atoms";
import { produce } from "immer";
import {
  AgingReportBalanceStatusType,
  AgingReportWithStatusesByMonth,
  getAgingReportStatuses,
} from "../util/getAgingReportStatus";
import { REPORTS_CHANGED_EVENT, REPORTS_STALE_EVENT, whenPageShown } from "../util/reportEvents";

export type { ReportsState };

type FacilityPayer = string;

const REFRESH_MS = 30 * 1000;
// While a conversation is open, new messages should show up within seconds
const LIVE_REFRESH_MS = 3 * 1000;

const calculateTotals = (reports: AgingReport[]) => {
  const totals: Record<Month, number> = { net: 0, prior: 0 };
  reports.forEach((report) => {
    for (const month in report.balances) {
      totals[month] = (totals[month] ?? 0) + (report.balances[month]?.history?.[0] ?? 0);
    }
    totals.net += report.net;
    totals.prior += report.prior;
  });
  return totals;
};

const getMonthsFromReports = (reports: AgingReport[]) => {
  const firstNotArchived = reports.find((r) => !r.archived);
  return Object.keys(firstNotArchived?.balances || {})
    .sort()
    .reverse();
};

const filterReports = (
  reports: AgingReportWithStatusesByMonth[],
  searchTerm: string | undefined,
  statusFilters: AgingReportBalanceStatusType[]
) => {
  const needle = searchTerm?.trim().toLowerCase();
  let filtered = reports;

  if (!statusFilters.includes(AgingReportBalanceStatusType.Archived)) {
    filtered = filtered.filter((r) => !r.archived);
  }

  if (statusFilters.length) {
    filtered = filtered.filter((r) => {
      if (!r.statusesByMonth) return false;
      const allStatuses = new Set(
        Object.values(r.statusesByMonth).flatMap((statuses) => Object.keys(statuses))
      );
      return statusFilters.every((status) => allStatuses.has(status));
    });
  }

  if (needle) {
    filtered = filtered.filter((report) =>
      JSON.stringify(report).toLowerCase().includes(needle)
    );
  }

  return filtered;
};

/**
 * Puts a report that was just changed on the server into the local copy. A separate hook so that screens which only
 * change reports do not also download all of them again.
 */
export const useReportUpdater = () => {
  const { facility, payer } = useUser();
  const setReportsPerFacilityPayer = useSetAtom(reportsPerFacilityPayerState);
  const facilityPayer: FacilityPayer = `${facility}#${payer}`;

  return useCallback(
    (report: AgingReport) => {
      setReportsPerFacilityPayer((previous) =>
        produce(previous, (draft) => {
          const target = draft[facilityPayer]?.reports;
          const index = target?.findIndex((r) => r.pk === report.pk && r.sk === report.sk);
          if (target && index !== undefined && index !== -1) {
            target[index] = {
              ...report,
              statusesByMonth: getAgingReportStatuses(report),
            };
          }
        })
      );
      window.dispatchEvent(new Event(REPORTS_CHANGED_EVENT));
    },
    [facilityPayer, setReportsPerFacilityPayer]
  );
};

export const useReports = ({ live = false }: { live?: boolean } = {}) => {
  const { invokeApi, facility, payer } = useUser();
  const [reportsPerFacilityPayer, setReportsPerFacilityPayer] = useAtom(
    reportsPerFacilityPayerState
  );
  const facilityPayer: FacilityPayer = `${facility}#${payer}`;
  const stored = reportsPerFacilityPayer?.[facilityPayer];
  const reports = useMemo(() => stored?.reports ?? [], [stored]);
  const [searchTerm, setSearchTerm] = useState<string>();
  const [statusFilters, setStatusFilters] = useState<
    AgingReportBalanceStatusType[]
  >([]);

  const displayableReports = useMemo(
    () => filterReports(reports, searchTerm, statusFilters),
    [reports, searchTerm, statusFilters]
  );
  const totals = useMemo(
    () => calculateTotals(displayableReports),
    [displayableReports]
  );

  // Cached reports show up immediately; this says when the current facility's fresh data has arrived (or failed to)
  const [loadedFor, setLoadedFor] = useState<string>();
  // The payer whose latest load failed, so an empty list is not mistaken for "no reports"
  const [failedFor, setFailedFor] = useState<string>();

  // Read through a ref so that a new revision does not recreate refresh and trigger another fetch
  const revision = useRef<string | null>(null);
  revision.current = stored?.reports?.length ? stored.revision ?? null : null;

  const load = useCallback(
    (silent: boolean) =>
      invokeApi({ endpoint: "/getReports", data: { revision: revision.current }, ...(silent && { silent }) })
        .then((response: any) => {
          // Our copy is current: nothing to download and nothing to recompute
          if (response?.unchanged) return setFailedFor(undefined);
          // Older servers answer with the bare list
          const fetched: AgingReport[] = Array.isArray(response) ? response : response.reports;
          const reports = fetched.map((report) => ({
            ...report,
            statusesByMonth: getAgingReportStatuses(report),
          })) as AgingReportWithStatusesByMonth[];
          setReportsPerFacilityPayer((previous) =>
            produce(previous, (draft) => {
              draft[facilityPayer] = {
                reports,
                months: getMonthsFromReports(reports),
                revision: Array.isArray(response) ? null : response.revision,
              };
            })
          );
          setFailedFor(undefined);
        })
        .catch((error: any) => {
          setFailedFor(facilityPayer);
          // invokeApi already reports request failures; anything else would otherwise leave the payer looking empty
          if (!error?.response) console.error("Could not process the reports", error);
        })
        .finally(() => setLoadedFor(facilityPayer)),
    [facilityPayer, invokeApi, setReportsPerFacilityPayer]
  );

  const refresh = useCallback(() => load(false), [load]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Other users' changes show up without a page reload; background requests stay silent
  useEffect(() => {
    // A slow download must not be followed by another request before it has finished
    let running = false;
    const poll = () => {
      if (running || document.visibilityState === "hidden") return;
      running = true;
      load(true).finally(() => {
        running = false;
      });
    };
    const timer = setInterval(poll, live ? LIVE_REFRESH_MS : REFRESH_MS);
    const stopWatchingPage = whenPageShown(poll);
    window.addEventListener(REPORTS_STALE_EVENT, poll);
    return () => {
      clearInterval(timer);
      stopWatchingPage();
      window.removeEventListener(REPORTS_STALE_EVENT, poll);
    };
  }, [load, live]);

  const updateReport = useReportUpdater();

  return {
    reports,
    displayableReports,
    totals,
    months: stored?.months,
    setSearchTerm,
    statusFilters,
    setStatusFilters,
    updateReport,
    refresh,
    loaded: loadedFor === facilityPayer,
    failed: failedFor === facilityPayer,
  };
};
